// Période du cockpit : année et sélection libre de mois (mois isolés, trimestres, cumul).

import { MONTHS_FULL } from './compute.js';
import type { CockpitEntity } from './types.js';

export const QUARTERS = [
  { key: 'T1', months: [1, 2, 3] },
  { key: 'T2', months: [4, 5, 6] },
  { key: 'T3', months: [7, 8, 9] },
  { key: 'T4', months: [10, 11, 12] },
] as const;

/** Mois valides (1 à 12), sans doublon, en ordre croissant. */
export function normalizeMonths(months: readonly number[]): number[] {
  return [...new Set(months.filter((m) => Number.isInteger(m) && m >= 1 && m <= 12))].sort((a, b) => a - b);
}

/** Lit « 1,2,3 » ; retombe sur le repli si la valeur est absente ou ne contient aucun mois valide. */
export function parseMonths(raw: string | null | undefined, fallback: readonly number[]): number[] {
  const parsed = normalizeMonths((raw ?? '').split(',').map((x) => Number(x.trim())));
  return parsed.length ? parsed : normalizeMonths(fallback);
}

/** Ajoute les mois s'ils ne sont pas tous sélectionnés, sinon les retire. Une sélection ne devient jamais vide. */
export function toggleMonths(selection: readonly number[], months: readonly number[]): number[] {
  const sel = normalizeMonths(selection);
  const all = months.every((m) => sel.includes(m));
  const next = all ? sel.filter((m) => !months.includes(m)) : normalizeMonths([...sel, ...months]);
  return next.length ? next : sel;
}

/** Mois de janvier au dernier mois clôturé. */
export const ytdMonths = (lastClosed: number): number[] =>
  Array.from({ length: Math.max(1, Math.min(12, lastClosed)) }, (_, i) => i + 1);

const sameMonths = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((m, i) => m === b[i]);

const cap = (s: string) => `${s[0]!.toUpperCase()}${s.slice(1)}`;

/** Libellé d'une période : « T2 2026 », « Janvier à juillet 2026 », « Mars, mai et juillet 2026 ». */
export function describePeriod(year: number, months: readonly number[]): string {
  const ms = normalizeMonths(months);
  if (!ms.length) return String(year);
  if (ms.length === 12) return `Année ${year}`;
  const q = QUARTERS.find((x) => sameMonths(x.months, ms));
  if (q) return `${q.key} ${year}`;
  const first = ms[0]!;
  const last = ms[ms.length - 1]!;
  if (ms.length === 1) return `${cap(MONTHS_FULL[first - 1]!)} ${year}`;
  if (last - first + 1 === ms.length)
    return `${cap(MONTHS_FULL[first - 1]!)} à ${MONTHS_FULL[last - 1]!.toLowerCase()} ${year}`;
  const names = ms.map((m) => MONTHS_FULL[m - 1]!.toLowerCase());
  const head = names.slice(0, -1).join(', ');
  return `${cap(head)} et ${names[names.length - 1]} ${year}`;
}

/**
 * Un mois entre dans le budget une fois échu : la facturation se concentre en fin de mois, le CA d'un mois en cours
 * est donc incomplet et le comparer à un budget au prorata des jours fausserait l'écart.
 * Retourne 1 pour un mois échu (passé, ou dernier jour atteint), 0 pour le mois en cours et les mois à venir.
 * Sans date de référence, tous les mois comptent.
 */
export function elapsedShare(year: number, month: number, asOf: string | null | undefined): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(asOf ?? '');
  if (!m) return 1;
  const [ay, am, ad] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (year !== ay) return year < ay ? 1 : 0;
  if (month < am) return 1;
  if (month > am) return 0;
  return ad >= new Date(Date.UTC(year, month, 0)).getUTCDate() ? 1 : 0;
}

export interface PeriodValues {
  /** Montants en M€ ; null quand la donnée n'existe pas pour la période. */
  ca: number | null;
  caBudget: number | null;
  marge: number | null;
  margeBudget: number | null;
  rex: number | null;
  rexBudget: number | null;
  margePct: number | null;
  tauxRex: number | null;
  tauxRexBudget: number | null;
  /** Tous les mois choisis sont clôturés en comptabilité, sur l'année du reporting. */
  closed: boolean;
  caFrom: 'sync' | 'fin' | null;
  /** Le budget laisse de côté le mois en cours ou des mois à venir de la période. */
  budgetToDate: boolean;
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

/**
 * Valeurs d'une période libre : somme des mois choisis.
 * - CA : SYNC pour tout mois et toute année disponibles, sinon comptabilité sur les mois clôturés.
 * - Marge, REX et budgets : comptabilité de l'année du reporting, mois tous clôturés ; sinon null.
 * - Budget : budget annuel (atterrissage) ÷ 12 par mois, pour l'année du reporting seulement, limité aux
 *   mois échus à la date `asOf` (le mois en cours et les mois à venir ne comptent pas).
 */
export function periodValues(
  e: CockpitEntity,
  finYear: number,
  year: number,
  months: readonly number[],
  asOf?: string | null,
): PeriodValues {
  const ms = normalizeMonths(months);
  const sameYear = year === finYear;
  const idx = ms.map((m) => e.monthsAvail.indexOf(m));
  const closed = sameYear && ms.length > 0 && idx.every((i) => i >= 0);
  const finSum = (cum: number[]) => sum(idx.map((i) => (cum[i] ?? 0) - (i > 0 ? (cum[i - 1] ?? 0) : 0)));

  const syncMonths = e.caSync?.[String(year)];
  let ca: number | null = null;
  let caFrom: PeriodValues['caFrom'] = null;
  if (e.caSync) {
    ca = sum(ms.map((m) => syncMonths?.[m - 1] ?? 0)) / 1e6;
    caFrom = 'sync';
  } else if (closed) {
    ca = finSum(e.monthly.ca.actual);
    caFrom = 'fin';
  }
  const marge = closed ? finSum(e.monthly.margeBrute.actual) : null;
  const rex = closed ? finSum(e.monthly.rex.actual) : null;
  const share = sameYear ? ms.reduce((t, m) => t + elapsedShare(year, m, asOf), 0) / 12 : 0;
  const budget = (annual: number) => (sameYear && share > 0 ? annual * share : null);
  const caBudget = budget(e.atterrissage.ca);
  const rexBudget = budget(e.atterrissage.rex);
  return {
    ca,
    caBudget,
    marge,
    margeBudget: budget(e.atterrissage.margeBrute),
    rex,
    rexBudget,
    margePct: ca && marge !== null ? (marge / ca) * 100 : null,
    tauxRex: ca && rex !== null ? (rex / ca) * 100 : null,
    tauxRexBudget: caBudget && rexBudget !== null ? (rexBudget / caBudget) * 100 : null,
    closed,
    caFrom,
    budgetToDate: sameYear && share * 12 < ms.length,
  };
}

export interface CaTrend {
  /** Mois affichés : 1 à 12. */
  months: number[];
  /** CA cumulé en M€, null au-delà du dernier mois disponible. */
  actual: (number | null)[];
  /** Budget annuel linéarisé, cumulé en M€. */
  budget: number[];
  /** Dernier mois pour lequel le réel existe, 0 si aucun. */
  lastMonth: number;
  from: 'sync' | 'fin' | null;
}

/**
 * Courbe du CA cumulé sur les 12 mois de l'année du reporting.
 * SYNC fournit le réel jusqu'au dernier mois facturé ; sans SYNC, la comptabilité s'arrête au dernier mois clôturé.
 */
export function caTrend(e: CockpitEntity, finYear: number): CaTrend {
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const budget = months.map((m) => (e.atterrissage.ca * m) / 12);
  const sync = e.caSync?.[String(finYear)];
  const lastSync = sync ? (months.filter((m) => sync[m - 1]).pop() ?? 0) : 0;
  if (sync && lastSync) {
    let run = 0;
    const actual = months.map((m) => {
      run += sync[m - 1] ?? 0;
      return m <= lastSync ? run / 1e6 : null;
    });
    return { months, actual, budget, lastMonth: lastSync, from: 'sync' };
  }
  const actual = months.map((m) => {
    const i = e.monthsAvail.indexOf(m);
    return i >= 0 ? (e.monthly.ca.actual[i] ?? null) : null;
  });
  const lastMonth = months.filter((m) => actual[m - 1] !== null).pop() ?? 0;
  return { months, actual, budget, lastMonth, from: lastMonth ? 'fin' : null };
}
